import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from '@/components';
import { ModalProps } from '@mui/base/Modal';
import clsx from 'clsx';
import { I18N_LANGUAGES } from '@/i18n';
import { useTranslateTestimonialContentMutation } from '@/services/TestimonialService';
import { useSnackbar } from 'notistack';
import { useIntl } from 'react-intl';
import { TestimonialFormData } from '@/types/testimonial.types';

type TestimonialTranslationMap = Record<
  string,
  Partial<{ content: string; author_title: string }>
>;

export interface TestimonialPreviewModalProps extends Omit<
  ModalProps,
  'children'
> {
  formData: Partial<TestimonialFormData & { translations?: unknown }>;
  source_language?: string;
  initialTranslations?: TestimonialTranslationMap;
  onTranslationsUpdate?: (translations: TestimonialTranslationMap) => void;
  onTranslationsGenerated?: (translations: TestimonialTranslationMap) => void;
}

interface LanguagePreviewProps {
  language: {
    code: string;
    label: string;
    flag: string;
  };
  isSource: boolean;
  authorName?: string;
  authorTitle?: string;
  baseContent?: string;
  translatedContent?: {
    content?: string;
    author_title?: string;
  };
  isLoading: boolean;
  onEditContent?: (
    languageCode: string,
    field: keyof EditValues,
    value: string
  ) => void;
}

interface EditValues {
  content?: string;
  author_title?: string;
}

const LanguagePreview: React.FC<LanguagePreviewProps> = ({
  language,
  isSource,
  authorName,
  authorTitle,
  baseContent,
  translatedContent,
  isLoading,
  onEditContent,
}) => {
  const intl = useIntl();
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState<EditValues>({});

  const data = translatedContent || {
    content: baseContent,
    author_title: authorTitle,
  };

  useEffect(() => {
    const initialValues: EditValues = {};
    if (data?.content) initialValues.content = data.content;
    if (data?.author_title) initialValues.author_title = data.author_title;
    setEditValues(initialValues);
  }, [data?.content, data?.author_title]);

  const toggleEditMode = () => {
    if (!isEditing) {
      const initialValues: EditValues = {};
      if (data?.content) initialValues.content = data.content;
      if (data?.author_title) initialValues.author_title = data.author_title;
      setEditValues(initialValues);
    }
    setIsEditing(!isEditing);
  };

  const handleChange = (field: keyof EditValues, value: string) => {
    setEditValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    if (onEditContent) {
      Object.entries(editValues).forEach(([field, value]) => {
        onEditContent(language.code, field as keyof EditValues, value);
      });
      setIsEditing(false);
    }
  };

  return (
    <div className="bg-gray-100 shadow-sm hover:shadow-md transition-all duration-300 rounded-xl overflow-hidden mb-5 border border-gray-200">
      <div className="bg-gray-200 px-5 py-3 flex items-center justify-between border-b border-gray-100">
        <div className="flex items-center">
          <img
            src={language.flag}
            alt={language.label}
            className="w-6 h-6 mr-3 rounded-sm shadow-sm"
          />
          <h3 className="text-base font-medium text-gray-800 flex items-center">
            {language.label}
            {isSource && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Source
              </span>
            )}
            {isLoading && (
              <span className="ml-2 flex items-center text-xs text-blue-600 contrast:text-blue-200 font-medium">
                <svg className="animate-spin h-3 w-3 mr-1" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                translating...
              </span>
            )}
          </h3>
        </div>

        {onEditContent && !isLoading && !isSource && (
          <button
            onClick={toggleEditMode}
            className={`${isEditing
              ? 'bg-blue-100 dark:bg-blue-900'
              : 'bg-gray-100 dark:bg-gray-400'
              } text-blue-600 dark:text-blue-200 hover:text-blue-800 p-1.5 rounded-full hover:bg-blue-50 dark:hover:bg-blue-600 transition-colors`}
            title={isEditing ? 'Cancel editing' : 'Edit all fields'}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
              />
            </svg>
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="p-5 flex justify-center">
          <div className="animate-pulse flex space-x-4 w-full">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-5">
          {isEditing ? (
            <>
              <div className="space-y-6">
                <div className="col-span-1">
                  <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">
                    {intl.formatMessage({
                      id: 'TESTIMONIAL.FORM.AUTHOR_TITLE',
                    })}
                  </p>
                  <input
                    type="text"
                    value={editValues.author_title || ''}
                    onChange={(e) =>
                      handleChange('author_title', e.target.value)
                    }
                    className="w-full input p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-base border border-gray-300"
                    placeholder={intl.formatMessage({
                      id: 'TESTIMONIAL.FORM.AUTHOR_TITLE.PLACEHOLDER',
                    })}
                    autoComplete="off"
                  />
                </div>

                <div className="col-span-1">
                  <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">
                    {intl.formatMessage({ id: 'TESTIMONIAL.FORM.CONTENT' })}
                  </p>
                  <textarea
                    value={editValues.content || ''}
                    onChange={(e) => handleChange('content', e.target.value)}
                    className="w-full textarea p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-base border border-gray-300 min-h-[80px]"
                    placeholder={intl.formatMessage({
                      id: 'TESTIMONIAL.FORM.CONTENT.PLACEHOLDER',
                    })}
                    autoComplete="off"
                  />
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
                >
                  {intl.formatMessage({ id: 'COMMON.SAVE' })}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-4">
                <div>
                  <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">
                    {intl.formatMessage({ id: 'TESTIMONIAL.FORM.AUTHOR_NAME' })}
                  </p>
                  <p className="text-base font-medium text-gray-800">
                    {authorName || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">
                    {intl.formatMessage({
                      id: 'TESTIMONIAL.FORM.AUTHOR_TITLE',
                    })}
                  </p>
                  <p className="text-sm text-gray-600">
                    {data?.author_title || authorTitle || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider font-medium text-gray-500 mb-1">
                    {intl.formatMessage({ id: 'TESTIMONIAL.FORM.CONTENT' })}
                  </p>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">
                    {data?.content || baseContent || '-'}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const TestimonialPreviewModal: React.FC<TestimonialPreviewModalProps> = ({
  open,
  onClose,
  formData,
  source_language = 'en',
  initialTranslations,
  onTranslationsUpdate,
  onTranslationsGenerated,
  className,
}) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [translateTestimonialContent, { isLoading: isTranslating }] =
    useTranslateTestimonialContentMutation();

  // Prefer initialTranslations (loaded/persisted) over formData's internal translations
  const [translations, setTranslations] = useState<TestimonialTranslationMap>(
    () => {
      if (initialTranslations) return initialTranslations;
      const raw = formData.translations;
      if (!raw) return {};
      if (typeof raw === 'string') {
        try {
          return JSON.parse(raw) as TestimonialTranslationMap;
        } catch {
          return {};
        }
      }
      return raw as TestimonialTranslationMap;
    }
  );

  // Sync if parent updates initialTranslations (controlled flow)
  useEffect(() => {
    if (initialTranslations) {
      setTranslations(initialTranslations);
    }
  }, [initialTranslations]);

  const handleEditContent = useCallback(
    (languageCode: string, field: keyof EditValues, value: string) => {
      setTranslations((prev) => ({
        ...prev,
        [languageCode]: {
          ...(prev[languageCode] ?? {}),
          [field]: value,
        },
      }));
    },
    []
  );

  const currentFormDataRef = useRef(formData);
  const currentSourceLanguageRef = useRef(source_language);

  useEffect(() => {
    currentFormDataRef.current = formData;
    currentSourceLanguageRef.current = source_language;
  }, [formData, source_language]);

  const handleRegenerateTranslations = useCallback(async () => {
    const currentForm = currentFormDataRef.current;
    if (!currentForm.content?.trim()) {
      enqueueSnackbar('Please provide testimonial content to translate', {
        variant: 'warning',
      });
      return;
    }

    try {
      const payload = {
        testimonial_id: currentForm.id,
        content: currentForm.content ?? '',
        author_title: currentForm.authorTitle,
        source_language: currentSourceLanguageRef.current || 'en',
      };

      const response = await translateTestimonialContent(payload).unwrap();

      if (response.warning_message) {
        enqueueSnackbar(response.warning_message, {
          variant: 'warning',
        });
      } else if (response.translations) {
        const mapped: TestimonialTranslationMap = {};
        Object.entries(response.translations).forEach(([langCode, value]) => {
          const v = value as Partial<{
            content: string;
            author_title: string;
          }>;
          mapped[langCode] = {
            content: v.content,
            author_title: v.author_title,
          };
        });

        setTranslations(mapped);
        if (!response.warning_message) {
          enqueueSnackbar('Translations regenerated successfully', {
            variant: 'success',
          });
        }
      }
    } catch (error) {
      console.error('Testimonial translation failed:', error);
      enqueueSnackbar(
        intl.formatMessage({
          id: 'TESTIMONIAL.PREVIEW.TRANSLATION_ERROR',
        }),
        { variant: 'error' }
      );
    }
  }, [translateTestimonialContent, enqueueSnackbar, intl]);

  const handleApplyTranslations = useCallback(() => {
    // Standardized behavior: filter out source language before returning
    const cleaned = Object.fromEntries(
      Object.entries(translations)
        .filter(([key]) => key !== source_language)
        .map(([lang, content]) => [
          lang,
          {
            content: content.content ?? '',
            author_title: content.author_title ?? '',
          },
        ])
    );

    if (onTranslationsUpdate) {
      onTranslationsUpdate(cleaned);
    } else if (onTranslationsGenerated) {
      onTranslationsGenerated(cleaned);
    }
    enqueueSnackbar('Translations applied successfully!', {
      variant: 'success',
    });
    onClose?.({}, 'backdropClick');
  }, [
    translations,
    source_language,
    onTranslationsUpdate,
    onTranslationsGenerated,
    onClose,
    enqueueSnackbar,
  ]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={clsx(
        'harbor-preview-root !flex items-center justify-center',
        className
      )}
      aria-labelledby="testimonial-preview-modal-title"
    >
      <div className="harbor-preview-modal bg-gray-100 w-full max-w-4xl mx-4 rounded-xl shadow-xl flex flex-col overflow-hidden max-h-[90vh]">
        {/* Modal Header */}
        <div className="modal-header bg-gray-200 px-6 py-4 border-b border-gray-100">
          <div className="modal-header-row flex justify-between items-center w-full">
            <h2
              id="testimonial-preview-modal-title"
              className="text-xl font-bold text-gray-800 flex items-center"
            >
              <svg
                className="w-5 h-5 mr-2 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              {intl.formatMessage({ id: 'TESTIMONIAL.PREVIEW.TITLE' })}
            </h2>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleRegenerateTranslations}
                disabled={isTranslating}
                className="inline-flex items-center px-3 py-1.5 text-sm bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors disabled:opacity-50 font-medium"
              >
                {isTranslating ? 'Translating…' : 'Regenerate Translations'}
              </button>

              <button
                type="button"
                onClick={() => onClose?.({}, 'backdropClick')}
                className="modal-close text-gray-400 hover:text-gray-600 focus:outline-none p-1 rounded-full hover:bg-gray-300 transition-colors"
                aria-label={intl.formatMessage({ id: 'COMMON.CLOSE' })}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="p-6 overflow-y-auto custom-scrollbar flex-1">
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-950 contrast:bg-blue-950 border border-blue-200 dark:border-blue-900 contrast:border-blue-900 rounded-lg">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 mr-3 mt-0.5 text-blue-600 dark:text-blue-200 contrast:text-blue-200 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-blue-800 dark:text-blue-200 contrast:text-blue-200 leading-relaxed">
                  {intl.formatMessage({
                    id: 'TESTIMONIAL.PREVIEW.DESCRIPTION',
                  })}
                </p>
              </div>
            </div>
          </div>

          <div className="lang-list pr-2 custom-scrollbar">
            {I18N_LANGUAGES.map((lang) => (
              <LanguagePreview
                key={lang.code}
                language={lang}
                isSource={lang.code === source_language}
                authorName={formData.authorName}
                authorTitle={formData.authorTitle}
                baseContent={formData.content}
                translatedContent={translations[lang.code]}
                isLoading={isTranslating}
                onEditContent={handleEditContent}
              />
            ))}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 bg-gray-200 border-t border-gray-100">
          <div className="footer-actions-row flex justify-end gap-3">
            <button
              type="button"
              onClick={() => onClose?.({}, 'backdropClick')}
              className="inline-flex items-center px-4 py-2 bg-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-400 focus:outline-none shadow-sm transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApplyTranslations}
              disabled={isTranslating}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none shadow-sm transition-colors duration-200 disabled:opacity-50"
            >
              Apply Translations
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default TestimonialPreviewModal;
